import {useContext, useState, MutableRefObject, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {PlaylistItemEntity} from '../../../../../../../backend/responses/playlist-item-entity';
import {AlertModal} from '../../../../../../../components/alert-modal/alert-modal';
import {ContentPage} from '../../../../../../../components/content-page/content-page';
import {PaginatedTable} from '../../../../../../../components/paginated-table/paginated-table';
import {OfficeContext} from '../../../../../../../contexts/office/context';
import {OfficeContextType} from '../../../../../../../contexts/office/types';
import {SnackBarContext} from '../../../../../../../contexts/snack-bar/context';
import {SnackBarContextType} from '../../../../../../../contexts/snack-bar/types';
import {ScreenPaths} from '../../../../../../../enums/screen-path';
import {usePaginatedTable} from '../../../../../../../hooks/paginated-table';
import {translations} from '../../../../../../../i18n/translations';
import {AppLanguageTag} from '../../../../../../../i18n/types';
import {PaginatedTableData} from '../../../../../../../interfaces/paginated-table-data';
import {PlaylistsRepository} from '../../../../../../../repositories/playlists';
import {PageHeader} from '../../../../../../../components/page-header/page-header';
import i18n from 'i18next';

// Columns IDs
enum ColumnIdentifier {
  Name = 'name',
  Type = 'type',
  State = 'state',
}

export default function PlaylistItemEntitiesSystemAdministratorOfficeScreen() {
  // Get params
  // - id: playlist id
  // - itemId: playlist item id
  let {id, itemId}: {id?: string; itemId?: string} = useParams();

  // Get context
  const snackBarContext: SnackBarContextType = useContext(SnackBarContext);
  const officeContext: OfficeContextType = useContext(OfficeContext);

  // Hold state
  const [updatePlaylistItemEntityDialogOpened, setUpdatePlaylistItemEntityDialogOpened] = useState<{
    disableDialogOpened: boolean;
    enableDialogOpened: boolean;
  }>({disableDialogOpened: false, enableDialogOpened: false});

  // Hold ref
  const dataToUpdateRef: MutableRefObject<
    {entityId: string; entityName: string; playlistItemActiveFlag: boolean} | undefined
  > = useRef();

  // Translations
  const {t} = useTranslation();

  // Fetch entities
  const handleGetEntities = async (limit: number, skip: number, search?: string) => {
    // Check ids
    if (!id || !itemId) {
      throw new Error('Empty playlist or playlist item id.');
    }

    // Load entities
    const result: {totalCount: number; data: Array<PlaylistItemEntity>} =
      await PlaylistsRepository.instance.getPlaylistItemEntitiesAsSystemAdministrator(
        id,
        itemId,
        skip,
        limit,
        search
      );

    // Process data
    const processedData: PaginatedTableData<PlaylistItemEntity> = result.data.map((obj) => {
      // Return row values
      return {
        id: obj.entity.id || '',
        item: obj,
        avatarUrl: obj.entity.picture,
        enabled: obj.isActive === true ? true : false,
        toLink: `${ScreenPaths.Office}${ScreenPaths.Office_SystemAdministrator}${ScreenPaths.SystemAdministrator_Entities}/${obj.entity.id}`,
        columns: [
          {
            columnId: ColumnIdentifier.Name,
            value: obj.entity.name || '',
          },
          {
            columnId: ColumnIdentifier.Type,
            value: t(translations.entity_types[obj.entity.type]) || '',
          },
          {
            columnId: ColumnIdentifier.State,
            value:
              obj.isActive === true
                ? t(
                    translations.playlists.system_administrator.playlist_item.entities
                      .state_value_true
                  )
                : t(
                    translations.playlists.system_administrator.playlist_item.entities
                      .state_value_false
                  ),
          },
        ],
      };
    });

    // Return result
    return {
      data: processedData,
      totalCount: result.totalCount,
    };
  };

  // Use the paginated table hook
  const {
    data,
    isLoading,
    error,
    pagesCount,
    currentPage,
    currentSearch,
    handlePageChange,
    handleApplyFilters,
    handleUpdateCurrentSearch,
    handleUpdateItem,
  } = usePaginatedTable<PlaylistItemEntity>({
    fetchData: handleGetEntities,
  });

  const handleAskUpdatePlaylistItemEntityState = (entityId: string, value: boolean) => {
    // Get entity name
    const entityName: string | undefined = data
      .find((obj) => obj.id === entityId)
      ?.columns.find((obj) => obj.columnId === ColumnIdentifier.Name)?.value;

    // Set entity info for later
    dataToUpdateRef.current = {
      entityId: entityId,
      entityName: entityName || '',
      playlistItemActiveFlag: value,
    };

    // Open modal
    setUpdatePlaylistItemEntityDialogOpened({
      disableDialogOpened: !value,
      enableDialogOpened: value,
    });
  };

  const handleDeclineUpdatePlaylistItemEntityState = () => {
    // Close modal
    setUpdatePlaylistItemEntityDialogOpened({
      disableDialogOpened: false,
      enableDialogOpened: false,
    });

    // Clear
    dataToUpdateRef.current = undefined;
  };

  const handleConfirmUpdatePlaylistItemEntityState = () => {
    // Close modal
    setUpdatePlaylistItemEntityDialogOpened({
      disableDialogOpened: false,
      enableDialogOpened: false,
    });

    // Update
    handleUpdatePlaylistItemEntityState();
  };

  const handleUpdatePlaylistItemEntityState = () => {
    if (dataToUpdateRef.current) {
      // Keep values
      const entityId = dataToUpdateRef.current.entityId;
      const value = dataToUpdateRef.current.playlistItemActiveFlag;

      // Clear
      dataToUpdateRef.current = undefined;

      // Updated item
      let updatedItem = data.find((obj) => obj.id === entityId)?.item;
      if (updatedItem) {
        updatedItem.isActive = value;
      }

      // Update item in UI
      handleUpdateItem(
        entityId,
        [
          {
            updatedColumnId: ColumnIdentifier.State,
            updatedColumnValue: value
              ? t(
                  translations.playlists.system_administrator.playlist_item.entities
                    .state_value_true
                )
              : t(
                  translations.playlists.system_administrator.playlist_item.entities
                    .state_value_false
                ),
          },
        ],
        updatedItem,
        value
      );

      // Make request to the backend
      // Note: Don't await because it will take too much time to update
      continueUpdatePlaylistItemEntityState(id || '', itemId || '', entityId, value);
    } else {
      // Error
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(
          translations.playlists.system_administrator.playlist_item.entities.errors
            .update_state_item_error_message
        ),
      });

      // Not expected. Log.
      console.debug(
        '🔴 Could not update the state of the playlist item. Information was missing to perform the request.'
      );
    }
  };

  const continueUpdatePlaylistItemEntityState = async (
    playlistId: string,
    playlistItemId: string,
    entityId: string,
    value: boolean
  ) => {
    try {
      // Make request to the backend
      await PlaylistsRepository.instance.updatePlaylistItemActiveStateForEntityAsSystemAdministrator(
        entityId,
        playlistId,
        playlistItemId,
        value
      );
    } catch (error) {
      // Old flag
      const oldFlag: boolean = !value;

      // Updated item
      let updatedItem = data.find((obj) => obj.id === entityId)?.item;
      if (updatedItem) {
        updatedItem.isActive = oldFlag;
      }

      // Change item back
      handleUpdateItem(
        entityId,
        [
          {
            updatedColumnId: ColumnIdentifier.State,
            updatedColumnValue: oldFlag
              ? t(
                  translations.playlists.system_administrator.playlist_item.entities
                    .state_value_true
                )
              : t(
                  translations.playlists.system_administrator.playlist_item.entities
                    .state_value_false
                ),
          },
        ],
        updatedItem,
        oldFlag
      );

      // Error
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: value
          ? t(
              translations.playlists.system_administrator.playlist_item.entities.errors
                .enable_item_error_message
            )
          : t(
              translations.playlists.system_administrator.playlist_item.entities.errors
                .disable_item_error_message
            ),
      });

      // Log
      console.debug(
        `🔴 Could not update the state of the playlist item with id ${playlistItemId} to ${value} for entity with id ${entityId}.`,
        error
      );
    }
  };

  // Get playlist item name
  let playlistItemNameLocalized: string = '';
  switch (i18n.language) {
    case AppLanguageTag.ptPT:
      playlistItemNameLocalized =
        officeContext.state.playlistItemForm?.titleLocalized?.ptPT ||
        officeContext.state.playlistItemForm?.title ||
        '';
      break;
    case AppLanguageTag.esES:
      playlistItemNameLocalized =
        officeContext.state.playlistItemForm?.titleLocalized?.esES ||
        officeContext.state.playlistItemForm?.title ||
        '';
      break;
    case AppLanguageTag.glES:
      playlistItemNameLocalized =
        officeContext.state.playlistItemForm?.titleLocalized?.glES ||
        officeContext.state.playlistItemForm?.title ||
        '';
      break;
    case AppLanguageTag.enUS:
      playlistItemNameLocalized =
        officeContext.state.playlistItemForm?.titleLocalized?.enUS ||
        officeContext.state.playlistItemForm?.title ||
        '';
      break;
    case AppLanguageTag.ptBR:
      playlistItemNameLocalized =
        officeContext.state.playlistItemForm?.titleLocalized?.ptBR ||
        officeContext.state.playlistItemForm?.title ||
        '';
      break;
    default:
      playlistItemNameLocalized = officeContext.state.playlistItemForm?.title || '';
      break;
  }

  return (
    <ContentPage>
      <PageHeader
        showSearch={true}
        searchLabel={t(
          translations.playlists.system_administrator.playlist_item.entities
            .search_input_placeholder
        )}
        searchText={currentSearch}
        searchOnChange={(text: string) => {
          // Update text search
          handleUpdateCurrentSearch(text);
        }}
        searchOnSubmit={() => {
          // Query backend about this
          handleApplyFilters(1, currentSearch);
        }}
        disableSearch={isLoading}
      />
      <PaginatedTable
        avatar={{
          type: 'entity',
        }}
        emptyTableMessage={t(
          translations.playlists.system_administrator.playlist_item.entities.no_entities
        )}
        columns={[
          {
            columnId: ColumnIdentifier.Name,
            title: t(
              translations.playlists.system_administrator.playlist_item.entities.name_header
            ),
            flexGrow: 2,
          },
          {
            columnId: ColumnIdentifier.Type,
            title: t(
              translations.playlists.system_administrator.playlist_item.entities.type_header
            ),
            flexGrow: 1,
          },
          {
            columnId: ColumnIdentifier.State,
            title: t(
              translations.playlists.system_administrator.playlist_item.entities.state_header
            ),
            flexGrow: 1,
            button: {
              type: 'toggle',
              showColumnValue: true,
              toggle: {
                enabledValue: t(
                  translations.playlists.system_administrator.playlist_item.entities
                    .state_value_true
                ),
                handleClick: handleAskUpdatePlaylistItemEntityState,
              },
            },
          },
        ]}
        rows={data}
        isLoading={isLoading}
        error={error}
        pagesCount={pagesCount}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
        linkableRows={true}
      />
      <AlertModal
        id="confirm-disable-playlist-item-entity-state"
        opened={updatePlaylistItemEntityDialogOpened.disableDialogOpened}
        title={t(
          translations.playlists.system_administrator.playlist_item.entities
            .disable_item_dialog_title,
          {
            name: playlistItemNameLocalized,
          }
        )}
        content={t(
          translations.playlists.system_administrator.playlist_item.entities
            .disable_item_dialog_message,
          {
            name: dataToUpdateRef.current?.entityName || '',
          }
        )}
        handleClose={handleDeclineUpdatePlaylistItemEntityState}
        actions={[
          {
            label: t(
              translations.playlists.system_administrator.playlist_item.entities
                .disable_item_dialog_action_yes_button
            ),
            type: 'destructive',
            handleClick: handleConfirmUpdatePlaylistItemEntityState,
          },
          {
            label: t(
              translations.playlists.system_administrator.playlist_item.entities
                .disable_item_dialog_action_no_button
            ),
            type: 'neutral',
            handleClick: handleDeclineUpdatePlaylistItemEntityState,
          },
        ]}
      />
      <AlertModal
        id="confirm-enable-playlist-item-entity-state"
        opened={updatePlaylistItemEntityDialogOpened.enableDialogOpened}
        title={t(
          translations.playlists.system_administrator.playlist_item.entities
            .enable_item_dialog_title,
          {
            name: playlistItemNameLocalized,
          }
        )}
        content={t(
          translations.playlists.system_administrator.playlist_item.entities
            .enable_item_dialog_message,
          {
            name: dataToUpdateRef.current?.entityName || '',
          }
        )}
        handleClose={handleDeclineUpdatePlaylistItemEntityState}
        actions={[
          {
            label: t(
              translations.playlists.system_administrator.playlist_item.entities
                .enable_item_dialog_action_no_button
            ),
            type: 'neutral',
            handleClick: handleDeclineUpdatePlaylistItemEntityState,
          },
          {
            label: t(
              translations.playlists.system_administrator.playlist_item.entities
                .enable_item_dialog_action_yes_button
            ),
            type: 'nondestructive',
            handleClick: handleConfirmUpdatePlaylistItemEntityState,
          },
        ]}
      />
    </ContentPage>
  );
}
